import { Injectable, inject } from '@angular/core';
import { TODO } from '@b2b/types/core';
import { OrdersCheckoutChargesInput } from '@b2b/types/order/api';
import { SecureCheckoutProps } from '@b2b/types/payment';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import _ from 'lodash';
import { ApiService } from '../api/api.service';
import { CartService } from '../cart/cart.service';
import { PaymentServiceEvents, PaymentState } from './payment.service.types';

/**
 * PaymentService (Angular 18)
 *
 * Replaces: apps/b2b-ui/common/services/payment/payment.service.ts
 * Manages payment state (type, method, charge amount, refund data) for the
 * checkout flow. Delegates HTTP calls to ApiService. Depends on CartService.
 *
 * Registered via downgradeInjectable in ng-downgraded-components.ts as 'PaymentService'.
 * Wave 4 — Main chain.
 */

function createInitialState(): PaymentState {
  return {
    state: 'loading',
    refund: { status: 'idle', data: null },
    process: {
      total: 0,
      chargeAmount: 0,
      paymentType: '',
      paymentMethod: '',
    },
  };
}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private apiService = inject(ApiService);
  // CartService injected for future use (legacy compatibility)
  private cartService = inject(CartService);

  private stateSubject = new BehaviorSubject<PaymentState>(createInitialState());
  state$ = this.stateSubject.asObservable();
  processData$ = this.state$.pipe(
    map((state) => state.process),
    distinctUntilChanged(),
  );

  secureCheckoutProps: Partial<SecureCheckoutProps> = {};
  secureCheckoutData: TODO;

  get isCreditCard(): boolean {
    return this.stateSubject.value.process.paymentMethod === 'CreditCard';
  }

  send(event: PaymentServiceEvents): void {
    switch (event.type) {
      case 'Payment::Update::Reset': {
        this.stateSubject.next(createInitialState());
        break;
      }

      case 'Payment::Update::ChargeAmount': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          process: {
            ...this.stateSubject.value.process,
            chargeAmount: event.params.chargeAmount,
          },
        });
        break;
      }

      case 'Payment::Update::Total': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          process: {
            ...this.stateSubject.value.process,
            paymentType: event.params.paymentType,
            chargeAmount: event.params.chargeAmount,
            total: event.params.total,
          },
        });
        break;
      }

      case 'Payment::Update::PaymentType': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          process: {
            ...this.stateSubject.value.process,
            paymentType: event.params.paymentType,
            chargeAmount: event.params.chargeAmount,
          },
        });
        break;
      }

      case 'Payment::Update::PaymentMethod': {
        this.stateSubject.next({
          ...this.stateSubject.value,
          process: {
            ...this.stateSubject.value.process,
            paymentMethod: event.params.paymentMethod,
          },
        });
        break;
      }

      default: {
        break;
      }
    }
  }

  getProcessData(): PaymentState['process'] {
    return this.stateSubject.value.process;
  }

  getRefundData(props: OrdersCheckoutChargesInput): Promise<any> {
    this.stateSubject.next({
      ...this.stateSubject.value,
      refund: { status: 'loading', data: null },
    });

    return this.apiService.orders.checkout
      .charges(props)
      .then((response: any) => {
        this.stateSubject.next({
          ...this.stateSubject.value,
          refund: { status: 'success', data: response.data.payload },
        });

        return response.data.payload;
      })
      .catch((error: any) => {
        this.stateSubject.next({
          ...this.stateSubject.value,
          refund: { status: 'error', data: null },
        });

        throw error;
      });
  }

  setSecureCheckoutProps(props: SecureCheckoutProps): void {
    Object.assign(
      this.secureCheckoutProps,
      _.pickBy(props, (value) => !_.isNil(value)),
    );
  }

  getFraudShieldVerification(): Promise<void> {
    return this.apiService.orders.checkout.fraudShieldVerification(this.secureCheckoutProps).then((response: any) => {
      this.secureCheckoutData = response.data.payload;
    });
  }
}
